import { GidMatrix, IfcData } from "@/services/admin-services/models";
import { UseCase } from "@/services/global-services/models";
import { nanoid } from "nanoid";
import { DataTableCell, DataTableColumnDefinition, DataTableConfig, DataTableFootnote, DataTableRow } from "../Models";
import {
    ActionType,
    ClearCellFootnote,
    DataTableActions,
    EnsureEmptyRow,
    InitializeDataSource,
    InsertRow,
    MoveRowDown,
    MoveRowUp,
    PasteData,
    RemoveRow,
    RestoreDataSource,
    SetActiveDisciplines,
    SetCellContent,
    SetCellFootnote,
    SetColumnFilterFunction,
    SetRowFilterFunction,
    SetRowVisibility,
    SetTableCaption,
    SetUseCaseFilterFunction,
    UpdateConfig,
    UpdateFootnote,
} from "./DataTableActions";
import { DataTableState } from "./DataTableState";

const containsData = (rows: DataTableRow[]): boolean => {
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        for (let j = 0; j < row.cells.length; j++) {
            const cell = row.cells[j];
            // if (cell.value && cell.value.trim().length > 0) return true;

            if (cell.value) {
                if (Array.isArray(cell.value) && cell.value.length > 0) return true;
                if (typeof cell.value === "string" && cell.value.trim().length > 0) return true;
            }
        }
    }

    return false;
};

const DEFAULT_HIDDEN = ["DIN276", "Uniformat", "Uniforme"];

interface DataSource {
    rows: DataTableRow[];
    footnotes: DataTableFootnote[];
}

const fillFromDataSource = (
    matrix: GidMatrix | IfcData,
    matrixType: string | undefined,
    columns: DataTableColumnDefinition[]
): DataSource => {
    switch (matrixType) {
        case "gid":
            return fillGidTable(matrix as GidMatrix, columns);
        case "ifc":
            return fillIfcTable(matrix as IfcData, columns);
        default:
            return { rows: [], footnotes: [] };
    }
};

const fillIfcTable = (data: IfcData, columns: DataTableColumnDefinition[]): DataSource => {
    const dataset: DataTableRow[] = [];
    const footnotes: DataTableFootnote[] = [];

    const ifcLinksRow: DataTableRow = {
        id: "links",
        cells: [],
        metadata: {},
    };

    for (let i = 0; i < columns.length; i++) {
        const column = columns[i];
        const cell: DataTableCell = {
            id: `links_${i}`,
            value: "",
            footnotes: [],
        };

        if (column.metadata?.softwareId) {
            cell.id = `link_software_${column.metadata.softwareId}`;
            cell.metadata = { softwareId: column.metadata.softwareId };
        }

        ifcLinksRow.cells.push(cell);
    }

    dataset.push(ifcLinksRow);

    for (let i = 0; i < data.entries.length; i++) {
        const row: DataTableRow = {
            id: data.entries[i].id,
            cells: [],
            metadata: {},
        };

        if (data.entries[i].description) {
            row.metadata!.description = data.entries[i].description;
        }

        if (data.entries[i].disciplines) {
            row.metadata!.disciplines = data.entries[i].disciplines;
        }

        const entry = data.entries[i];

        for (let j = 0; j < columns.length; j++) {
            const column = columns[j];
            const cell: DataTableCell = {
                id: `${entry.id}_${j}`,
                value: j === 0 ? entry.label : "",
                footnotes: [],
            };

            if (column.metadata?.softwareId) {
                cell.id = `${entry.id}_software_${column.metadata.softwareId}`;
                cell.metadata = { softwareId: column.metadata.softwareId };

                const software = entry.softwareLabels.find((x) => x.software.id === column.metadata!.softwareId);
                if (software) {
                    cell.value = software.label;
                }
            } else if (column.metadata?.classificationId) {
                cell.id = `${entry.id}_classification_${column.metadata.classificationId}`;
                cell.metadata = { classificationId: column.metadata.classificationId };

                const classifications = entry.classificationItems?.filter(
                    (x) => x.software.id === column.metadata!.classificationId
                );
                if (classifications) {
                    cell.value = classifications.map((x) => x.label).join("<br />");
                }
            }

            row.cells.push(cell);
        }

        dataset.push(row);
    }

    return { rows: dataset, footnotes };
};

const fillGidTable = (matrix: GidMatrix, columns: DataTableColumnDefinition[]): DataSource => {
    const dataset: DataTableRow[] = [];

    const dataSourceGroups = matrix.sort((a, b) => a.groupCode.localeCompare(b.groupCode));
    const footnotes: DataTableFootnote[] = [];

    for (let i = 0; i < dataSourceGroups.length; i++) {
        const dataSourceGroup = dataSourceGroups[i];
        const dataSourceRows = dataSourceGroup.items.sort((a, b) => a.propertyCode.localeCompare(b.propertyCode));
        const softwareColumns = columns.filter((x) => Boolean(x.metadata?.softwareId));

        for (let j = 0; j < dataSourceRows.length; j++) {
            const attachedUsecases = dataSourceRows[j].attachedUsecases ?? [];
            const property = dataSourceRows[j];

            attachedUsecases.forEach((useCase) => {
                const row: DataTableRow = {
                    id: property.propertyCode,
                    cells: [],
                    metadata: {
                        groupCode: dataSourceGroup.groupCode,
                        groupName: dataSourceGroup.groupName,
                        groupDescription: dataSourceGroup.groupDescription,
                        disciplines: property.attachedToDisciplines,
                        useCases: [useCase],
                        description: property.description,
                        predefinedValues: property.valueDefinitions.map((x) => ({ name: x.name, code: x.code })),
                        propertySymbol: property.propertySymbol ?? "X",
                    },
                };

                row.cells.push({
                    id: `${property.propertyCode}_name`,
                    value: property.propertyName ?? "",
                    footnotes: [],
                });

                let defaultHidden = false;
                for (let k = 0; k < DEFAULT_HIDDEN.length; k++) {
                    const hiddenValue = DEFAULT_HIDDEN[k]?.toLowerCase();
                    if (property.propertyName && property.propertyName.toLocaleLowerCase().includes(hiddenValue)) {
                        defaultHidden = true;
                        break;
                    }
                }

                if (defaultHidden) {
                    row.metadata!.visible = false;
                }

                const gidColumnData = property.phases.sort((a, b) => a.gidNumber - b.gidNumber);
                for (let k = 0; k < gidColumnData.length; k++) {
                    const data = gidColumnData[k];

                    const entryFootnotes = data.footnotes || [];
                    for (let l = 0; l < entryFootnotes.length; l++) {
                        const footnote = entryFootnotes[l];

                        const existingFootnote = footnotes.find((x) => x.id === footnote.id);
                        if (existingFootnote) continue;

                        footnotes.push({
                            id: footnote.id,
                            value: footnote.value,
                            notetype: footnote.notetype,
                        });
                    }

                    row.cells.push({
                        id: `${property.propertyCode}_gid_${k + 1}`,
                        value: "",
                        footnotes: entryFootnotes.map((x) => x.id),
                        metadata: {
                            disciplines: data.disciplines,
                            useCases: data.disciplines.length > 0 ? attachedUsecases : [],
                        },
                    });
                }

                for (let k = 0; k < softwareColumns.length; k++) {
                    const softwareColumn = softwareColumns[k];
                    const softwareId = softwareColumn.metadata!.softwareId;
                    const softwareValue = property.software.find((x) => x.software === softwareId);

                    if (!softwareValue) {
                        row.cells.push({
                            id: `${property.propertyCode}_software_${softwareId}`,
                            value: "",
                            footnotes: [],
                            metadata: {
                                softwareId: softwareId,
                            },
                        });

                        continue;
                    }

                    const entryFootnotes = softwareValue.footnotes || [];
                    for (let l = 0; l < entryFootnotes.length; l++) {
                        const footnote = entryFootnotes[l];

                        const existingFootnote = footnotes.find((x) => x.id === footnote.id);
                        if (existingFootnote) continue;

                        footnotes.push({
                            id: footnote.id,
                            value: footnote.value,
                            notetype: footnote.notetype,
                        });
                    }

                    row.cells.push({
                        id: `${property.propertyCode}_software_${softwareValue.software}`,
                        value: softwareValue.label ?? "",
                        footnotes: entryFootnotes.map((x) => x.id),
                    });
                }

                dataset.push(row);
            });
        }
    }

    return { rows: dataset, footnotes };
};

export const DataTableReducer = (state: DataTableState, action: DataTableActions): DataTableState => {
    switch (action.type) {
        case ActionType.InitializeDataSource: {
            if (!state.dataSource) return state;
            const { rows, footnotes } = fillFromDataSource(state.dataSource, state.dataSourceType, state.columns);
            const parsedDataSource = rows.map((row) => ({
                ...row,
                metadata: { ...row.metadata },
                cells: row.cells.map((cell) => ({ ...cell })),
            }));

            if (containsData(state.rows)) {
                const manualFootnotes = state.footnotes.filter((x) => !footnotes.find((y) => y.id === x.id));

                return { ...state, parsedDataSource, footnotes: [...footnotes, ...manualFootnotes] };
            }

            return { ...state, parsedDataSource, rows, footnotes };
        }

        case ActionType.RestoreDataSource: {
            if (!state.dataSource) return state;

            const rows: DataTableRow[] = [];
            for (let i = 0; i < state.parsedDataSource.length; i++) {
                const sourceRow = state.parsedDataSource[i];
                const currentRow = state.rows.find((x) => x.id === sourceRow.id);

                if (sourceRow.id === "links") {
                    rows.push({
                        ...state.rows[0],
                        id: sourceRow.id,
                        cells: state.rows[0].cells.map((cell, index) => ({ ...cell, id: sourceRow.cells[index].id })),
                    });
                    continue;
                }

                if (!currentRow) {
                    rows.push({ ...sourceRow, cells: sourceRow.cells.map((cell) => ({ ...cell })) });
                    continue;
                }

                const row: DataTableRow = { ...sourceRow, metadata: { ...sourceRow.metadata }, cells: [] };

                if (action.payload.ignoreVisibility) {
                    const visible = currentRow.metadata?.visible;
                    if (row.metadata) {
                        row.metadata.visible = row.metadata?.visible === false && !visible ? false : visible;
                    }
                } else {
                    if (row.metadata && row.metadata.visible !== false) {
                        delete row.metadata.visible;
                    }
                }

                for (let j = 0; j < sourceRow.cells.length; j++) {
                    const sourceCell = sourceRow.cells[j];
                    const currentCell = currentRow.cells.find((x) => x.id === sourceCell.id);

                    if (!currentCell) {
                        row.cells.push({ ...sourceCell });
                        continue;
                    }

                    let cell: DataTableCell;
                    if (action.payload.ignoreFootnotes) {
                        cell = { ...currentCell };
                    } else {
                        cell = { ...currentCell, footnotes: [...sourceCell.footnotes] };
                    }

                    if (!action.payload.ignoreChanges && sourceRow.id !== "links") {
                        cell.value = sourceCell.value;
                    } else {
                        if (cell.modified !== true) {
                            cell.modified = false;
                            cell.value = sourceCell.value;
                        }
                    }

                    cell.metadata = { ...sourceCell.metadata };

                    row.cells.push(cell);
                }

                rows.push(row);
            }

            return { ...state, rows, refreshCount: state.refreshCount + 1 };
        }

        case ActionType.UpdateConfig: {
            const { config } = action.payload;
            return { ...state, title: config.title, columns: config.columns };
        }

        case ActionType.PasteData: {
            const rows: DataTableRow[] = JSON.parse(JSON.stringify(state.rows));
            const rowIndex = action.payload.row ? rows.findIndex((x) => x.id === action.payload.row) : 0;

            for (let i = rowIndex, j = 0; j < action.payload.data.length; i++, j++) {
                const row = rows[i];
                const pastedRow = { ...action.payload.data[j] };

                if (row) {
                    row.id = pastedRow.id;
                    row.cells = [...pastedRow.cells];
                } else {
                    rows.push(pastedRow);
                }
            }

            return { ...state, rows };
        }

        case ActionType.EnsureEmptyRow: {
            const rows = [...state.rows];
            if (rows.length > 0) return state;

            const newRow: DataTableRow = { id: nanoid(), cells: [] };
            for (let i = 0; i < state.columns.length; i++) {
                newRow.cells.push({
                    id: nanoid(),
                    value: "",
                    footnotes: [],
                    focus: false,
                });
            }

            rows.push(newRow);

            return { ...state, rows };
        }

        case ActionType.InsertRow: {
            const rows = [...state.rows];
            const newRow: DataTableRow = { id: nanoid(), cells: [] };
            for (let i = 0; i < state.columns.length; i++) {
                const column = state.columns[i];
                const cell: DataTableCell = {
                    id: nanoid(),
                    value: "",
                    footnotes: [],
                    focus: action.payload.focus ? i === 0 : false,
                };

                if (column.metadata?.softwareId) {
                    cell.id = `${newRow.id}_software_${column.metadata.softwareId}`;
                    cell.metadata = { softwareId: column.metadata.softwareId };
                }

                newRow.cells.push(cell);
            }

            rows.push(newRow);

            return { ...state, rows };
        }

        case ActionType.RemoveRow: {
            const rows = state.rows.filter((x) => x.id !== action.payload.id);

            if (rows.length === 0) {
                const newRow: DataTableRow = { id: nanoid(), cells: [] };
                for (let i = 0; i < state.columns.length; i++) {
                    newRow.cells.push({ id: nanoid(), value: "", footnotes: [], focus: i === 0 });
                }

                rows.push(newRow);
            }

            return { ...state, rows };
        }

        case ActionType.SetRowVisibility: {
            const rows = [...state.rows];
            const row = rows.find((x) => x.id === action.payload.id);
            if (!row) return state;
            row.metadata = { ...row.metadata, visible: action.payload.visible };
            return { ...state, rows };
        }

        case ActionType.MoveRowDown: {
            const rows = [...state.rows];
            const index = rows.findIndex((x) => x.id === action.payload.id);
            rows.splice(index + 1, 0, rows.splice(index, 1)[0]);

            return { ...state, rows };
        }

        case ActionType.MoveRowUp: {
            const rows = [...state.rows];
            const index = rows.findIndex((x) => x.id === action.payload.id);
            rows.splice(index - 1, 0, rows.splice(index, 1)[0]);

            return { ...state, rows };
        }

        case ActionType.SetCellContent: {
            const rows = [...state.rows];
            const row = rows.find((x) => x.id === action.payload.row);
            if (!row) return state;
            const cell = row.cells.find((x) => x.id === action.payload.cell);
            if (!cell) return state;
            cell.value = action.payload.value;

            const sourceRow = state.parsedDataSource.find((x) => x.id === action.payload.row);
            if (sourceRow) {
                const sourceColumn = sourceRow.cells.find((x) => x.id === action.payload.cell);
                if (sourceColumn) {
                    cell.modified = sourceColumn.value !== action.payload.value;
                }
            }

            delete cell.focus;

            return { ...state, rows };
        }

        case ActionType.AddCellFootnote: {
            const footnotes = [...state.footnotes];
            if (footnotes.findIndex((x) => x.id === action.payload.footnote.id) === -1) {
                footnotes.push(action.payload.footnote);
            }

            const rows = [...state.rows];
            const row = rows.find((x) => x.id === action.payload.row);
            if (!row) return state;
            const cell = row.cells.find((x) => x.id === action.payload.cell);
            if (!cell) return state;
            cell.footnotes = cell.footnotes.filter((x) => x !== action.payload.footnote.id);
            cell.footnotes.push(action.payload.footnote.id);

            return { ...state, rows, footnotes };
        }

        case ActionType.UpdateFootnote: {
            const footnotes = [...state.footnotes];
            const update = footnotes.find((x) => x.id === action.payload.id);
            if (!update) return state;
            update.value = action.payload.value;

            return { ...state, footnotes };
        }

        case ActionType.ClearCellFootnotes: {
            const rows = [...state.rows];
            const row = rows.find((x) => x.id === action.payload.row);
            if (!row) return state;
            const cell = row.cells.find((x) => x.id === action.payload.cell);
            if (!cell) return state;
            cell.footnotes = [];

            return { ...state, rows };
        }

        case ActionType.SetColumnFilterFunction: {
            return { ...state, onFilterColumn: action.payload.func };
        }

        case ActionType.SetRowFilterFunction: {
            return { ...state, onFilterRow: action.payload.func };
        }

        case ActionType.SetUseCaseFilterFunction: {
            return { ...state, onFilterUseCase: action.payload.func };
        }

        case ActionType.SetActiveDisciplines: {
            return { ...state, activeDisciplines: action.payload.disciplines };
        }

        case ActionType.SetTableCaption: {
            return { ...state, caption: action.payload.caption };
        }

        default:
            return state;
    }
};

export const initializeDataSource = (): InitializeDataSource => ({
    type: ActionType.InitializeDataSource,
});

export const restoreDataSource = (
    ignoreChanges: boolean,
    ignoreVisibility: boolean,
    ignoreFootnotes: boolean
): RestoreDataSource => ({
    type: ActionType.RestoreDataSource,
    payload: { ignoreChanges, ignoreVisibility, ignoreFootnotes },
});

export const updateConfig = (config: DataTableConfig): UpdateConfig => ({
    type: ActionType.UpdateConfig,
    payload: { config },
});

export const pasteData = (data: DataTableRow[], row?: string): PasteData => ({
    type: ActionType.PasteData,
    payload: { row, data },
});

export const ensureEmptyRow = (): EnsureEmptyRow => ({
    type: ActionType.EnsureEmptyRow,
});

export const insertRow = (focus: boolean = true): InsertRow => ({
    type: ActionType.InsertRow,
    payload: { focus },
});

export const removeRow = (id: string): RemoveRow => ({
    type: ActionType.RemoveRow,
    payload: { id },
});

export const setRowVisibility = (id: string, visible: boolean): SetRowVisibility => ({
    type: ActionType.SetRowVisibility,
    payload: { id, visible },
});

export const moveRowDown = (id: string): MoveRowDown => ({
    type: ActionType.MoveRowDown,
    payload: { id },
});

export const moveRowUp = (id: string): MoveRowUp => ({
    type: ActionType.MoveRowUp,
    payload: { id },
});

export const setCellContent = (row: string, cell: string, value: string): SetCellContent => ({
    type: ActionType.SetCellContent,
    payload: { row, cell, value },
});

export const addCellFootnote = (row: string, cell: string, footnote: DataTableFootnote): SetCellFootnote => ({
    type: ActionType.AddCellFootnote,
    payload: { row, cell, footnote },
});

export const updateFootnote = (id: string, value: string): UpdateFootnote => ({
    type: ActionType.UpdateFootnote,
    payload: { id, value },
});

export const clearCellFootnote = (row: string, cell: string): ClearCellFootnote => ({
    type: ActionType.ClearCellFootnotes,
    payload: { row, cell },
});

export const setColumnFilterFunction = (
    func: (col: DataTableColumnDefinition) => boolean
): SetColumnFilterFunction => ({
    type: ActionType.SetColumnFilterFunction,
    payload: { func },
});

export const setRowFilterFunction = (func: (row: DataTableRow) => boolean): SetRowFilterFunction => ({
    type: ActionType.SetRowFilterFunction,
    payload: { func },
});

export const setUseCaseFilterFunction = (func: (useCase: UseCase) => boolean): SetUseCaseFilterFunction => ({
    type: ActionType.SetUseCaseFilterFunction,
    payload: { func },
});

export const setActiveDisciplines = (disciplines: string[]): SetActiveDisciplines => ({
    type: ActionType.SetActiveDisciplines,
    payload: { disciplines },
});

export const setTableCaption = (caption: string): SetTableCaption => ({
    type: ActionType.SetTableCaption,
    payload: { caption },
});
